import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Music } from "lucide-react";

// Sunehra — Lost Stories × Jai Dhir. The label blocks embedding on the official and
// acoustic uploads (error 150), so start with the animated music video; keep fallbacks.
const VIDEO_CANDIDATES = ["wKuKod7VlP0", "xLkkX2j5BNk", "3IsdDhy8nA8", "ldxLcAqLlxg"];

const Equalizer = () => (
    <span className="flex h-4 items-end gap-[3px]" aria-hidden="true">
        {[0.9, 0.6, 1.1].map((dur, i) => (
            <motion.span
                key={i}
                className="w-[3px] origin-bottom rounded-full bg-obsidian"
                animate={{ scaleY: [0.3, 1, 0.45, 1, 0.3] }}
                transition={{ duration: dur, repeat: Infinity, ease: "easeInOut" }}
                style={{ height: "100%" }}
            />
        ))}
    </span>
);

const MusicPlayer = () => {
    const playerRef = useRef(null);
    const candidateIdx = useRef(0);
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        const init = () => {
            if (playerRef.current || !document.getElementById("yt-music-frame")) return;
            playerRef.current = new window.YT.Player("yt-music-frame", {
                videoId: VIDEO_CANDIDATES[0],
                playerVars: {
                    autoplay: 1,
                    controls: 0,
                    disablekb: 1,
                    playsinline: 1,
                    modestbranding: 1,
                    rel: 0,
                },
                events: {
                    onReady: (e) => {
                        // Try true unmuted autoplay first (works where the browser allows it)
                        e.target.setVolume(55);
                        e.target.playVideo();
                        // If the browser blocks it, fall back to muted autostart;
                        // sound then unlocks on the guest's first touch anywhere
                        setTimeout(() => {
                            const p = playerRef.current;
                            if (p && p.getPlayerState && ![1, 3].includes(p.getPlayerState())) {
                                p.mute();
                                p.playVideo();
                            }
                        }, 2500);
                    },
                    onStateChange: (e) => {
                        if (e.data === window.YT.PlayerState.PLAYING) setPlaying(true);
                        if (e.data === window.YT.PlayerState.PAUSED) setPlaying(false);
                        if (e.data === window.YT.PlayerState.ENDED) e.target.playVideo();
                    },
                    onError: (e) => {
                        console.warn("Music player error code:", e.data);
                        candidateIdx.current += 1;
                        const next = VIDEO_CANDIDATES[candidateIdx.current];
                        if (next) e.target.loadVideoById(next);
                    },
                },
            });
            window.__ytPlayer = playerRef.current;
        };

        if (window.YT && window.YT.Player) {
            init();
        } else {
            const prev = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (prev) prev();
                init();
            };
            if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                const tag = document.createElement("script");
                tag.src = "https://www.youtube.com/iframe_api";
                document.body.appendChild(tag);
            }
        }
    }, []);

    // Sound unlocks on the guest's first touch anywhere on the page
    useEffect(() => {
        const kick = () => {
            const p = playerRef.current;
            if (!p || !p.getPlayerState) return;
            p.unMute();
            p.setVolume(55);
            if (p.getPlayerState() !== 1) p.playVideo();
        };
        window.addEventListener("pointerdown", kick, { once: true });
        window.addEventListener("keydown", kick, { once: true });
        return () => {
            window.removeEventListener("pointerdown", kick);
            window.removeEventListener("keydown", kick);
        };
    }, []);

    const toggle = () => {
        const p = playerRef.current;
        if (!p || !p.playVideo) return;
        if (playing) {
            p.pauseVideo();
        } else {
            p.unMute();
            p.setVolume(55);
            p.playVideo();
        }
    };

    return (
        <>
            <div
                className="pointer-events-none fixed left-[-9999px] top-0 h-px w-px overflow-hidden"
                aria-hidden="true"
            >
                <div id="yt-music-frame" />
            </div>

            <div className="fixed bottom-6 right-6 z-[70] flex items-center gap-3">
                <motion.span
                    data-testid="now-playing-chip"
                    initial={false}
                    animate={{ opacity: playing ? 1 : 0, x: playing ? 0 : 12 }}
                    transition={{ duration: 0.5 }}
                    className="pointer-events-none hidden rounded-full border border-gold/30 bg-obsidian/85 px-4 py-2 font-cinzel text-[9px] font-semibold uppercase tracking-[0.25em] text-gold backdrop-blur-md sm:block"
                >
                    Sunehra · Lost Stories × Jai Dhir
                </motion.span>
                <button
                    data-testid="audio-toggle-button"
                    onClick={toggle}
                    aria-label={playing ? "Pause music" : "Play Sunehra — Lost Stories × Jai Dhir"}
                    className={`relative flex h-14 w-14 items-center justify-center rounded-full bg-gold text-obsidian shadow-[0_0_30px_rgba(229,184,42,0.4)] transition-transform duration-500 hover:scale-110 ${
                        playing ? "" : "animate-pulse"
                    }`}
                >
                    {playing ? <Equalizer /> : <Music size={20} />}
                </button>
            </div>
        </>
    );
};

export default MusicPlayer;
